<template>
   <div>
      <v-card v-if="form.membertype !== 'HON'">
         <v-card-title class="payment-card-title">
            <div>
               <div class="payment-title">Membership Payments</div>
               <div class="payment-subtitle">Track online payment, submitted receipts, and admin-recorded payments in one place.</div>
            </div>
         </v-card-title>
         <v-card-subtitle>
            <p>You can choose to pay using ToyyibPay payment gateway. This will take you to the secure online payment process.</p>
            <p>If you have already paid using other methods, you can register your payment receipt by clicking on the 'Register Receipt'
            button and enter the relevant information which includes your payment receipt as proof of the payment. Make sure you print out
            your receipt in PDF or take a snapshot photo of your receipt if it is in paper form.</p>
         </v-card-subtitle>
         <v-card-text>
           <v-alert v-if="paymentMessage" dense text :type="paymentMessageType" class="mb-4">
               {{ paymentMessage }}
            </v-alert>
            <v-alert v-if="cpdRenewalBlockedMessage" dense text type="warning" class="mb-4">
               {{ cpdRenewalBlockedMessage }}
            </v-alert>
            <div v-if="hasOutstandingAnnualFees" class="outstanding-payment-card">
               <div class="outstanding-payment-header">
                  <div>
                     <div class="outstanding-eyebrow">Outstanding Annual Fees</div>
                     <div class="outstanding-heading">{{ outstanding.year_count }} year(s) pending payment</div>
                     <div class="outstanding-copy">
                        Please settle the overdue membership year below to keep your payment record up to date.
                     </div>
                  </div>
                  <div class="outstanding-total">
                     <span>Total Outstanding</span>
                     <strong>RM {{ formatMoney(outstanding.amount) }}</strong>
                  </div>
               </div>
               <v-simple-table dense class="outstanding-year-table">
                  <template v-slot:default>
                     <thead>
                        <tr>
                           <th class="text-left">Year</th>
                           <th class="text-right">Annual Fee</th>
                           <th class="text-left">Status</th>
                           <th class="text-right">Action</th>
                        </tr>
                     </thead>
                     <tbody>
                        <tr v-for="year in outstanding.years" :key="'outstanding-' + year">
                           <td>
                              <div class="outstanding-year">{{ year }}</div>
                              <div class="outstanding-note">Annual subscription</div>
                           </td>
                           <td class="text-right payment-amount">RM {{ formatMoney(outstanding.annual_fee) }}</td>
                           <td>
                              <v-chip small dark :color="yearStatusColor(year)">{{ yearStatusLabel(year) }}</v-chip>
                              <div v-if="isYearPayable(year) && !canRenewYear(year)" class="cpd-lock-note">
                                 {{ cpdLockText(year) }}
                              </div>
                           </td>
                           <td class="text-right">
                              <template v-if="!isPendingReviewYear(year)">
                                 <v-btn small color="primary" :loading="submitting && selectedPayYear === year" :disabled="submitting" @click="requestYearPayment('gateway', year)">
                                    Pay Online
                                 </v-btn>
                                 <v-btn small outlined color="primary" class="ml-2" :disabled="submitting" @click="requestYearPayment('manual', year)">
                                    Register Receipt
                                 </v-btn>
                              </template>
                              <span v-else class="muted-text">Receipt under review</span>
                           </td>
                        </tr>
                     </tbody>
                  </template>
               </v-simple-table>
            </div>
            <v-simple-table class="payment-ledger-table">
               <template v-slot:default>
                  <thead>
                     <tr>
                        <th class="text-left">Payment For</th>
                        <th class="text-left">Method</th>
                        <th class="text-left">Payment Date</th>
                        <th class="text-right">Amount</th>
                        <th class="text-left">Receipt No.</th>
                        <th class="text-left">Reference</th>
                        <th class="text-left">Status</th>
                        <th class="text-left">Receipt</th>
                     </tr>
                  </thead>
                  <tbody v-if="(payments.length > 0)">
                     <tr
                        v-for="item in payments"
                        :key="item.id"
                     >
                        <td>
                           <div class="payment-purpose">{{ paymentPurpose(item) }}</div>
                           <div class="payment-description">{{ item.description || '-' }}</div>
                        </td>
                        <td>
                           <v-chip small outlined :color="paymentMethodColor(item)">
                              {{ paymentMethodLabel(item) }}
                           </v-chip>
                        </td>
                        <td>{{ formatDate(item.paymentdate || item.created_at) }}</td>
                        <td class="text-right payment-amount">RM {{ Number(item.amount || 0).toFixed(2) }}</td>
                        <td>
                           <div class="payment-reference">{{ item.receipt_no || '-' }}</div>
                        </td>
                        <td>
                           <div class="payment-reference">{{ item.trxnid || '-' }}</div>
                           <div class="payment-year">Year {{ item.payyear || '-' }}</div>
                        </td>
                        <td>
                           <v-chip small dark class="payment-status-chip" :color="paymentStatusColor(item)">
                              {{ paymentStatusLabel(item) }}
                           </v-chip>
                           <div v-if="item.decline_reason" class="payment-decline-note">
                              <div class="payment-decline-label">
                                 <v-icon x-small color="#991b1b" class="mr-1">mdi-alert-circle-outline</v-icon>
                                 Remarks
                              </div>
                              <div class="payment-decline-text">{{ item.decline_reason }}</div>
                           </div>
                        </td>
                        <td>
                           <a v-if="hasReceipt(item)" :href="receiptUrl(item)" target="_blank" rel="noopener" class="receipt-link">
                              View receipt
                           </a>
                           <span v-else class="muted-text">-</span>
                        </td>
                     </tr>
                  </tbody>
                  <tbody v-if="(payments.length === 0)">
                     <tr>
                        <td colspan="8" class="text-center empty-payment-state">
                           No payment record yet.
                        </td>
                     </tr>
                  </tbody>
               </template>
            </v-simple-table>
         </v-card-text>
         <v-card-actions>
            <div style="width:100%">
               <!-- STATE 1: Ada pending ToyyibPay (belum expired) -->
                <div v-if="haspendinggatewaypayment && canMakePayment && !haspendingpayment">
                  <v-alert type="warning" dense text>
                     You have an incomplete payment session.
                     <strong v-if="timeRemaining"> Expires in: {{ timeRemaining }}</strong>
                  </v-alert>
                  <v-btn color="warning" @click="dopayment('gateway')" :disabled="submitting">
                     Resume Payment
                  </v-btn>
                  <v-btn color="grey darken-1" dark @click="showConfirmNewPayment = true" :disabled="submitting">
                     Start New Payment
                  </v-btn>

                  <!-- Confirm dialog -->
                   <v-dialog v-model="showConfirmNewPayment" max-width="400px">
                     <v-card>
                        <v-card-title>Start New Payment?</v-card-title>
                        <v-card-text>
                           This will cancel your current pending payment session and start a new one. Are you sure?
                        </v-card-text>
                        <v-card-actions>
                           <v-spacer></v-spacer>
                           <v-btn color="primary" @click="confirmStartNew">Yes, Start New</v-btn>
                           <v-btn @click="showConfirmNewPayment = false">Cancel</v-btn>
                        </v-card-actions>
                     </v-card>
                   </v-dialog>
                </div>

                <!-- STATE 1.5: Lepas cancel pending, tunjuk pilihan -->
                 <div v-else-if="showPaymentOptions && canMakePayment && !haspaid">
                  <v-alert type="info" dense text>
                     Previous payment cancelled. How would you like to proceed?
                  </v-alert>
                  <v-btn color="primary" @click="initiateOnlinePayment" :disabled="submitting">
                     Pay with ToyyibPay
                  </v-btn>
                  <v-btn color="primary" class="ml-2" @click="dopayment('manual')" :disabled="submitting">
                     Register Receipt
                  </v-btn>
                   <br><br>

                 </div>

                <!-- STATE 2: Tiada pending, boleh bayar -->
                 <div v-else-if="canMakePayment && !haspendingpayment && !haspaid">
                  <v-btn color="primary" @click="dopayment('gateway')" :disabled="submitting">
                     Pay with ToyyibPay
                  </v-btn>
                  <v-btn color="primary" class="ml-2" @click="dopayment('manual')" :disabled="submitting">
                     Register Receipt
                  </v-btn>
                  <br><br>
                 </div>

                 <!-- STATE 3: Manual receipt pending admin verify -->
                  <div v-if="haspendingpayment">
                     <v-alert type="info" dense text>
                        Your payment receipt is pending admin verification.
                     </v-alert>
                  </div>

                  <!-- STATE 3.5: Payment already recorded -->
                  <div v-if="haspaid">
                     <v-alert type="success" dense text>
                        Your payment has already been recorded.
                     </v-alert>
                  </div>

                  <!-- STATE 4: Membership masih valid -->
                   <div v-if="ismembershipstillvalid">
                     <v-alert type="success" dense text>{{ validmessage }}</v-alert>
                  </div>
                  <span v-if="!haspendinggatewaypayment" class="text-red-700">{{ invalidmessage }}</span>
            </div>
         </v-card-actions>
      </v-card>

      <v-dialog v-model="paymentRestrictionDialog" max-width="520px">
         <v-card>
            <v-card-title>{{ paymentRestrictionTitle }}</v-card-title>
            <v-card-text>{{ paymentRestrictionMessage }}</v-card-text>
            <v-card-actions>
               <v-spacer></v-spacer>
               <v-btn color="primary" text @click="paymentRestrictionDialog = false">OK</v-btn>
            </v-card-actions>
         </v-card>
      </v-dialog>

      <v-dialog v-model="dlgregreceipt" max-width="680px">
         <v-card class="receipt-dialog">
            <v-card-title class="receipt-dialog-title">
               <div>
                  <div class="receipt-dialog-heading">Register Payment Receipt</div>
                  <div class="receipt-dialog-subtitle">Submit your manual payment proof for admin verification.</div>
               </div>
               <v-spacer></v-spacer>
               <v-chip small color="warning" text-color="white">Pending Review</v-chip>
            </v-card-title>
            <v-card-text>
               <v-alert dense text type="info" class="mb-5">
                  Your receipt will be listed as Pending until it is reviewed and approved by the Secretariat.
               </v-alert>
               <v-form ref="receiptform" lazy-validation v-model="formvalid">
                  <v-row dense>
                     <v-col cols="12" md="7">
                        <v-text-field label="Payment For" v-model="form.description" readonly outlined dense></v-text-field>
                     </v-col>
                     <v-col cols="12" md="5">
                        <v-select label="Member Type" v-model="form.membertype" :items="membertypes" item-text="description" item-value="membertype" readonly outlined dense></v-select>
                     </v-col>
                     <v-col cols="12" md="5">
                        <v-text-field label="Amount (RM)" v-model="form.amount" readonly outlined dense></v-text-field>
                     </v-col>
                     <v-col cols="12" md="7">
                        <v-text-field
                           label="Transaction / Receipt No."
                           v-model="form.trxnid"
                           outlined
                           dense
                           :rules="trxnRules"
                           hint="Use the reference number shown on your receipt."
                           persistent-hint
                           required
                        ></v-text-field>
                     </v-col>
                     <v-col cols="12">
                        <v-file-input
                           label="Attach Receipt"
                           accept=".jpg, .jpeg, .png, .pdf"
                           v-model="form.document"
                           show-size
                           outlined
                           dense
                           prepend-icon="mdi-paperclip"
                           :rules="fileRules"
                           hint="PDF, JPG, or PNG only."
                           persistent-hint
                           required
                        ></v-file-input>
                     </v-col>
                  </v-row>
               </v-form>
            </v-card-text>
            <v-card-actions class="receipt-dialog-actions">
               <v-alert v-if="paymentMessage" dense text :type="paymentMessageType" class="receipt-dialog-alert">
                  {{ paymentMessage }}
               </v-alert>
               <v-spacer></v-spacer>
               <v-btn text @click="dlgregreceipt = false" :disabled="submitting">Cancel</v-btn>
               <v-btn color="primary" @click="registerreceipt" :loading="submitting" :disabled="submitting || !formvalid">
                  Submit Receipt
               </v-btn>
            </v-card-actions>
         </v-card>
      </v-dialog>
      <br>
      <v-divider inset></v-divider>
      <v-card>
         <v-card-title>Entrance and Subscription Fees</v-card-title>
         <v-card-subtitle>
            <p>Subscription fees shall be based on the different classes of membership as follows</p>
         </v-card-subtitle>
         <v-card-text>
            <v-simple-table dense>
               <template v-slot:default>
                  <thead>
                     <tr class="grey darken-2">
                        <th class="text-left" width="20"><span class="text-white">No.</span></th>
                        <th class="text-left"><span class="text-white">Classes of Membership</span></th>
                        <th class="text-left" width="150"><span class="text-white">Entrance Fees</span></th>
                        <th class="text-left" width="200"><span class="text-white">Subscription Fees (Annual)</span></th>
                     </tr>
                  </thead>
                  <tbody>
                     <tr class="grey lighten-3"
                        v-for="item in membertypes"
                        :key="item.no"
                     >
                        <td>{{ item.no }}</td>
                        <td>{{ item.description }}</td>
                        <td>{{ item.entfee }}</td>
                        <td>{{ item.subsfee }}</td>
                     </tr>
                  </tbody>
               </template>
            </v-simple-table>
         </v-card-text>
      </v-card>
   </div>
</template>

<script>
const axios = require('axios')

export default {
   name: "TablePaymentSF",

   props: {
      purpose: { type: String, default: '' },
   },

   data() {
      return {
         user: Object,
         membership: Object,
         userid: 0,
         payments: [],
         outstanding: {
            status: 'clear',
            annual_fee: 0,
            years: [],
            pending_review_years: [],
            year_states: {},
            next_payable_year: null,
            year_count: 0,
            amount: 0,
            label: 'No overdue fee',
         },
         haspaid: false,
         haspendingpayment: false,
         haspendinggatewaypayment: false,
         pendingExpiresAt: null,
         timeRemaining: '',
         timerInterval: null,
         showConfirmNewPayment: false,
         showPaymentOptions: false,
         onlinePaymentAvailable: true,
         submitting: false,
         formvalid: false,
         ismembershipstillvalid: false,
         validmessage: '',
         invalidmessage: '',
         paymentMessage: '',
         paymentMessageType: 'info',
         paymentRestrictionDialog: false,
         paymentRestrictionTitle: '',
         paymentRestrictionMessage: '',
         dlgregreceipt: false,
         form: {
            description: "Subscription Fee",
            membertype: "",
            paymentdate: "",
            amount: 0.00,
            trxnid: "",
            document: null
         },
         selectedPayYear: null,
         membertypes: [
            { no: 1, membertype: "HON", description: "Honorary Fellow", efamount: 0, sfamount: 0, entfee: "None", subsfee: "None" },
            { no: 2, membertype: "FEL", description: "Fellow", efamount: 200.00, sfamount: 200.00, entfee: "None", subsfee: "RM 200.00" },
            { no: 3, membertype: "COR", description: "Corporate", efamount: 300.00, sfamount: 200.00, entfee: "RM 100.00", subsfee: "RM 200.00" },
            { no: 4, membertype: "GRA", description: "Graduate", efamount: 150.00, sfamount: 100.00, entfee: "RM 50.00", subsfee: "RM 100.00" },
            { no: 5, membertype: "STU", description: "Student", efamount: 30.00, sfamount: 0.00, entfee: "RM 30.00", subsfee: "None" },
            { no: 6, membertype: "ASC", description: "Associate", efamount: 200.00, sfamount: 100.00, entfee: "RM 100.00", subsfee: "RM 100.00" }
         ],
         trxnRules: [
            v => (v !== "") || "Transaction No. is required",
         ],
         fileRules: [
            v => (v !== null) || "Must attach your receipt in PDF or image format"
         ],
         polling: null,
      }
   },

   mounted: function () {
      this.user = this.$page.user
      this.$emit('PaymentMounted')
   },

   beforeDestroy: function () {
	   clearInterval(this.polling)
      clearInterval(this.timerInterval)
   },

   computed: {
      canMakePayment() {
         if (this.isCorporateUpgradeIntent() || this.isGraduateUpgradeIntent() || this.isNewApplicationIntent()) return !this.haspaid
         return !this.ismembershipstillvalid && this.canRenewYear(this.getPayYear())
      },
      hasOutstandingAnnualFees() {
         return this.outstanding
            && Array.isArray(this.outstanding.years)
            && this.outstanding.years.length > 0
      },
      cpdRenewalBlockedMessage() {
         if (this.ismembershipstillvalid || this.currentPaymentType() !== 'SF' || !this.isCorporateMember()) return ''
         var year = this.getPayYear()
         if (this.canRenewYear(year)) return ''
         return this.cpdLockText(year)
      },
   },

   methods: {
      setuserid(pUserID) {
         this.userid = pUserID
         this.initialize()
      },

      showPaymentMessage(type, message) {
         this.paymentMessageType = type
         this.paymentMessage = message || ''
      },

      showPaymentRestriction(title, message) {
         this.paymentRestrictionTitle = title
         this.paymentRestrictionMessage = message
         this.paymentRestrictionDialog = true
      },

      initialize() {
         this.$nextTick(() => {
            this.loadmemberinfo()
         })
      },

      formatDate(e) {
         if (!e) return '-'
         return this.$moment(new Date(e)).format('DD/MM/YYYY @ h:mm:ss A')
      },

      formatMoney(value) {
         return Number(value || 0).toFixed(2)
      },

      statusText(e) {
         if (e === '01') return 'Success'
         if (e === '00') return 'Waiting Admin Verification'
         if (e === '02') return 'Pending'
         if (e === '03') return 'Failed'
         return e || '-'
      },

      paymentStatusLabel(item) {
         var status = String(item.trxnstatus || '').padStart(2, '0')
         if (status === '01') return 'Success'
         if (status === '03') return item.decline_reason ? 'Declined' : 'Failed'
         if (status === '00') return 'Pending Review'
         return 'Pending'
      },

      paymentStatusColor(item) {
         var status = String(item.trxnstatus || '').padStart(2, '0')
         if (status === '01') return '#047857'
         if (status === '03') return '#b91c1c'
         if (status === '00') return '#b45309'
         return '#92400e'
      },

      receiptUrl(item) {
         if (item.tabletype === 'renewals') return '/member/payment/renewal/receipt/' + encodeURIComponent(item.id)
         return '/member/payment/receipt/' + encodeURIComponent(item.id)
      },

      hasReceipt(item) {
         if (String(item.trxnstatus || '').padStart(2, '0') !== '01') return false
         return item.tabletype === 'renewals'
            || !!item.receipt_no
            || !!(item.document && item.documentpath)
            || String(item.paymentmethod || '').toUpperCase() === 'TOYYIBPAY'
      },

      paymentMethodLabel(item) {
         var method = String(item.paymentmethod || '').toUpperCase()
         if (method === 'TOYYIBPAY' || item.tabletype === 'renewals') return 'ToyyibPay'
         if (method === 'MANUAL_RECEIPT') return 'Manual Receipt'
         if (method === 'MANUAL') {
            return String(item.trxnstatus || '') === '01' ? 'Manual by Admin' : 'Manual Receipt'
         }
         if (item.document && item.documentpath) return 'Manual Receipt'
         if (String(item.trxnid || '').toUpperCase().indexOf('MANUAL') === 0) return 'Manual by Admin'
         return 'ToyyibPay'
      },

      paymentMethodColor(item) {
         var label = this.paymentMethodLabel(item)
         if (label === 'ToyyibPay') return 'primary'
         if (label === 'Manual by Admin') return 'success'
         return 'deep-purple'
      },

      paymentPurpose(item) {
         var description = String(item.description || '').toLowerCase()
         if (description.indexOf('corporate upgrade') >= 0) return 'Corporate Upgrade'
         if (description.indexOf('graduate upgrade') >= 0) return 'Graduate Upgrade'
         if (String(item.paymenttype || '') === 'SF') return 'Annual Fee'
         if (String(item.paymenttype || '') === 'EFSF') return 'Entrance Fee + Annual Fee'
         if (String(item.paymenttype || '') === 'EF') return 'Entrance Fee'
         return item.paymenttype || 'Membership Fee'
      },

      isCorporateMember() {
         return this.membership && this.membership.membertype === 'COR'
      },

      isCorporateUpgradeIntent() {
         return this.membership
            && this.requestedPurpose() === 'corporate-upgrade'
            && this.membership.membertype === 'GRA'
            && parseInt(this.membership.memberstatus || 0) === 3
            && parseInt(this.membership.applcorpstatus || 0) === 5
            && parseInt(this.membership.applcorp_proposer_status || 0) === 1
            && parseInt(this.membership.applcorp_seconder_status || 0) === 1
      },

      cpdEligibility(year) {
         var eligibility = this.outstanding && this.outstanding.cpd_eligibility
            ? this.outstanding.cpd_eligibility
            : {}
         return eligibility[String(year)] || eligibility[Number(year)] || null
      },

      canRenewYear(year) {
         if (this.currentPaymentType() !== 'SF' || !this.isCorporateMember()) return true
         var eligibility = this.cpdEligibility(year)
         return !!(eligibility && eligibility.eligible)
      },

      cpdLockText(year) {
         var eligibility = this.cpdEligibility(year) || {}
         var points = Number(eligibility.points || 0)
         var required = Number(eligibility.required_points || 12)
         var cpdYear = Number(eligibility.cpd_year || (Number(year) - 1))
         return 'Renewal is locked because CPD points for ' + cpdYear + ' are ' + points + '/' + required + '. Minimum 12 CPD points are required for Corporate renewal.'
      },

      loadmemberinfo() {
         if (this.user.usertype === 'members') {
            var url = '/member/info/' + this.userid
            axios.get(url)
            .then((resp) => {
               var rtn = resp.data
               if (rtn.status === 'OK') {
                  this.membership = rtn.data
                  this.form.membertype = this.isCorporateUpgradePayment() ? 'COR' : this.membership.membertype
                  this.testvalid(this.membership)
                  this.setamount(this.form.membertype)
                  this.form.description = this.currentPaymentDescription()
                  this.loadpaymentinfo()
               }
            })
         }
      },

      loadpaymentinfo() {
         axios.post('/member/payment/list', {
            userid: this.userid,
            paymenttype: 'EFSF',
            purpose: this.requestedPurpose()
         })
         .then((resp) => {
            if (resp.data) {
               var rtn = resp.data
               this.outstanding = rtn.outstanding || this.outstanding
               this.onlinePaymentAvailable = rtn.online_payment_available !== false
               if (rtn.status === 'OK') {
                  var currentPayYear = this.getPayYear().toString()
                  this.payments = this.uniquePayments(rtn.data || [])
                  var currentPayments = this.payments.filter(item => this.isCurrentPayment(item, currentPayYear))
                  if (currentPayments.length > 0) {
                     var pendingGateway = currentPayments.filter(item => item.trxnstatus === '02')
                     this.haspaid = currentPayments.some(item => item.trxnstatus === '01')
                     this.haspendingpayment = currentPayments.some(item => item.trxnstatus === '00')
                     this.haspendinggatewaypayment = pendingGateway.length > 0
                     
                     if (pendingGateway.length > 0) {
                        // Ambil yang latest
                        var latest = pendingGateway.reduce((a, b) => a.id > b.id ? a : b)
                        var createdAt = this.$moment(latest.paymentdate || latest.created_at)
                        var expiresAt = createdAt.clone().add(60, 'minutes')

                        if (this.$moment().isBefore(expiresAt)) {
                           this.haspendinggatewaypayment = true
                           this.pendingExpiresAt = expiresAt
                           this.startTimer()
                        } else {
                           // Dah expired kat frontend — tunjuk butang bayar biasa
                           this.haspendinggatewaypayment = false
                        }
                     } else {
                        this.haspendinggatewaypayment = false
                     }
                  } else {
                     this.haspaid = false
                     this.haspendingpayment = false
                     this.haspendinggatewaypayment = false
                  }

                  if (this.haspaid) { this.$emit('PaymentCompleted') }
                  this.$emit('PaymentReady', this.haspaid || this.haspendingpayment)
               } else {
                  this.haspaid = false
                  this.haspendingpayment = false
                  this.haspendinggatewaypayment = false
                  this.showPaymentOptions = false
                  this.$emit('PaymentReady', false)
               }
            }
         })
      },

      uniquePayments(items) {
         var seen = {}
         return (items || []).filter((item) => {
            var key = [item.tabletype || 'payments', item.id, item.paymenttype, item.payyear].join('-')
            if (seen[key]) return false
            seen[key] = true
            return true
         })
      },

      // Timer countdown
      startTimer() {
         if (this.timerInterval) clearInterval(this.timerInterval)
         this.timerInterval = setInterval(() => {
            if (!this.pendingExpiresAt) return
            var diff = this.$moment(this.pendingExpiresAt).diff(this.$moment())
            if (diff <= 0) {
               clearInterval(this.timerInterval)
               this.timeRemaining = ''
               this.haspendinggatewaypayment = false
               this.loadpaymentinfo() // refresh senarai
            } else {
               var duration = this.$moment.duration(diff)
               var mins = String(Math.floor(duration.asMinutes())).padStart(2, '0')
               var secs = String(duration.seconds()).padStart(2, '0')
               this.timeRemaining = mins + ':' + secs
            }
         }, 1000)
      },

      // Confirm start new payment
      confirmStartNew() {
         this.showConfirmNewPayment = false
         // Cancel semua pending gateway dulu
         var pendingItems = this.payments.filter(item => item.trxnstatus === '02' && this.isCurrentPayment(item, this.getPayYear().toString()))
         var cancelPromises = pendingItems.map(item => axios.post('/member/payment/cancel-pending', { id: item.id, tabletype: item.tabletype }))
         Promise.all(cancelPromises).then(() => {
            this.haspendinggatewaypayment = false
            clearInterval(this.timerInterval)
            this.timeRemaining = ''
            this.showPaymentOptions = true 
            // Terus initiate payment baru
            // this.initiateOnlinePayment()
         })
      },

      cancelPendingAndPay() {
         // Cancel semua record '02' untuk payyear semasa, pastu buat bill baru
         var pendingItems = this.payments.filter(item =>
            item.trxnstatus === '02' && this.isCurrentPayment(item, this.getPayYear().toString())
         )

         // Update status ke '03' (failed/cancelled) untuk semua pending gateway bills
         var cancelPromises = pendingItems.map(item => {
            axios.post('/member/payment/cancel-pending', {
               id: item.id,
               tabletype: item.tabletype
            })
         })

         Promise.all(cancelPromises).then(() => {
            // Selepas semua pending dibatalkan, initiate payment baru
            this.haspendinggatewaypayment = false
            this.initiateOnlinePayment()
         })
      },

      currentPaymentType() {
         if (this.isCorporateUpgradePayment() || this.isCorporateUpgradeIntent() || this.isGraduateUpgradeIntent() || this.isNewApplicationIntent()) return 'EFSF'
         return this.membership && parseInt(this.membership.memberstatus) === 2 ? 'EFSF' : 'SF'
      },

      yearState(year) {
         var states = this.outstanding && this.outstanding.year_states
            ? this.outstanding.year_states
            : {}
         return states[String(year)] || states[Number(year)] || 'locked'
      },

      isPendingReviewYear(year) {
         return this.yearState(year) === 'pending_review'
      },

      isYearPayable(year) {
         return this.yearState(year) === 'payable'
      },

      yearStatusLabel(year) {
         if (this.isPendingReviewYear(year)) return 'Pending Review'
         if (!this.isYearPayable(year)) return 'Previous Year Required'
         if (!this.canRenewYear(year)) return 'CPD Required'
         return 'Outstanding'
      },

      yearStatusColor(year) {
         if (this.isPendingReviewYear(year)) return '#b45309'
         if (!this.isYearPayable(year)) return '#64748b'
         if (!this.canRenewYear(year)) return '#92400e'
         return '#880808'
      },

      requestYearPayment(method, year) {
         if (this.isPendingReviewYear(year)) {
            this.showPaymentRestriction(
               'Receipt Pending Review',
               'Your payment receipt for ' + year + ' has been submitted and is pending Secretariat review.'
            )
            return
         }

         if (!this.isYearPayable(year)) {
            var requiredYear = this.outstanding.next_payable_year
            this.showPaymentRestriction(
               'Previous Outstanding Fee Required',
               'Please settle or submit payment proof for the outstanding fee for ' + requiredYear + ' before proceeding with payment for ' + year + '.'
            )
            return
         }

         if (!this.canRenewYear(year)) {
            this.showPaymentRestriction('Renewal Requirement', this.cpdLockText(year))
            return
         }

         this.dopayment(method, year)
      },

      isCorporateUpgradePayment() {
         return (this.membership
            && parseInt(this.membership.memberstatus) === 2
            && parseInt(this.membership.applcorpstatus || 0) === 2) || this.isCorporateUpgradeIntent()
      },

      isGraduateUpgradePayment() {
         return (this.membership
            && this.membership.membertype === 'GRA'
            && parseInt(this.membership.memberstatus) === 2
            && parseInt(this.membership.applgradstatus || 0) === 2) || this.isGraduateUpgradeIntent()
      },

      isGraduateUpgradeIntent() {
         return this.membership
            && this.requestedPurpose() === 'graduate-upgrade'
            && this.membership.membertype === 'STU'
            && parseInt(this.membership.memberstatus || 0) === 3
            && parseInt(this.membership.applgradstatus || 0) === 5
            && parseInt(this.membership.applgrad_proposer_status || 0) === 1
            && parseInt(this.membership.applgrad_seconder_status || 0) === 1
      },

      requestedPurpose() {
         if (this.purpose) return this.purpose
         return new URLSearchParams(window.location.search).get('purpose') || ''
      },

      isNewApplicationIntent() {
         return this.membership
            && this.requestedPurpose() === 'new-application'
            && parseInt(this.membership.memberstatus || 0) === 0
            && parseInt(this.membership.hasapplied || 0) !== 1
      },

      currentPaymentDescription() {
         if (this.isCorporateUpgradePayment()) return 'Corporate Upgrade Fee'
         if (this.isGraduateUpgradePayment()) return 'Graduate Upgrade Fee'
         return this.currentPaymentType() === 'EFSF' ? 'Entrance Fee + Subscription Fee' : 'Subscription Fee'
      },

      paymentMemberType() {
         if (this.isCorporateUpgradePayment()) return 'COR'
         if (this.isGraduateUpgradePayment()) return 'GRA'
         return this.form.membertype
      },

      isSameOrAfterDate(value, baseline) {
         if (!baseline) return true
         if (!value) return false
         return this.$moment(value).diff(this.$moment(baseline), 'seconds') >= 0
      },

      isCurrentPayment(item, payyear) {
         var samePaymentPeriod = String(item.payyear || '') === String(payyear)
            && String(item.paymenttype || '') === this.currentPaymentType()

         if (!samePaymentPeriod) return false

         if (this.isGraduateUpgradePayment()) {
            return String(item.membertype || '') === 'GRA'
               && this.isSameOrAfterDate(item.paymentdate || item.created_at, this.membership.applgrad_approved_at)
         }

         if (this.isCorporateUpgradePayment()) {
            return String(item.membertype || '') === 'COR'
               || String(item.description || '').toLowerCase().indexOf('corporate upgrade') >= 0
         }

         return true
      },

      setamount(e) {
         var rtntype = this.membertypes.find(obj => {
            return obj.membertype === e
         })
         this.form.amount = this.currentPaymentType() === 'EFSF' ? rtntype.efamount : rtntype.sfamount
      },

      preparePaymentContext(payyear) {
         this.selectedPayYear = payyear || null
         this.form.membertype = this.paymentMemberType()
         this.form.description = this.currentPaymentType() === 'EFSF' ? this.currentPaymentDescription() : 'Subscription Fee'
         this.setamount(this.form.membertype)
         this.form.trxnid = ''
         this.form.document = null
      },

      dopayment(e, payyear) {
         var targetYear = payyear || this.getPayYear()
         this.preparePaymentContext(targetYear)
         if (this.currentPaymentType() === 'SF' && this.hasOutstandingAnnualFees && !this.isYearPayable(targetYear)) {
            this.requestYearPayment(e, targetYear)
            return
         }
         if (!this.canRenewYear(this.paymentYearForSubmission())) {
            this.showPaymentRestriction('Renewal Requirement', this.cpdLockText(this.paymentYearForSubmission()))
            return
         }
         if (e === 'manual') {
            this.dlgregreceipt = true
            return
         }
         if (e === 'gateway') {
            this.initiateOnlinePayment(payyear)
            return
         }
      },

      getPayYear() {
         if (this.outstanding && this.outstanding.next_payable_year) {
            return Number(this.outstanding.next_payable_year)
         }
         var now = this.$moment()
         return now.year() + (now.month() >= 9 ? 1 : 0)
      },

      testvalid(pData) {
         if (pData) {
            if (this.isCorporateUpgradeIntent()) {
               this.invalidmessage = 'Please complete the Corporate Upgrade payment before submitting your application.'
               this.ismembershipstillvalid = false
               this.validmessage = ''
               return
            }
            if (this.isGraduateUpgradeIntent()) {
               this.invalidmessage = 'Please complete the Graduate Upgrade payment before submitting your application.'
               this.ismembershipstillvalid = false
               this.validmessage = ''
               return
            }
            if (this.isNewApplicationIntent()) {
               this.invalidmessage = 'Complete the membership registration payment before submitting your application.'
               this.ismembershipstillvalid = false
               this.validmessage = ''
               return
            }
            if (parseInt(pData.memberstatus) === 2) {
               this.invalidmessage = 'Your membership application has been approved. Please make payment by using either Pay Online or Register Receipt'
               this.ismembershipstillvalid = false
               return
            }
            if ((pData.membertype === 'HON') || (pData.membertype === 'STU')) {
               this.validmessage = 'Your membership is always active and does not require any subcription fee annually.'
               this.ismembershipstillvalid = true
               return
            }
            if (!pData.datevalid || !this.$moment(pData.datevalid).isValid()) {
               this.invalidmessage = 'Your membership validity date is not recorded. Please make payment by using either Pay Online or Register Receipt'
               this.ismembershipstillvalid = false
               this.validmessage = ''
               return
            }
            var datevalid = this.$moment(pData.datevalid)
            var datenow = this.$moment()
            var diff = datevalid.diff(datenow, 'days')
            if (diff <= 0) {
               this.invalidmessage = 'Your membership validity has passed. Please make necessary payment by using either Pay Online or Register Receipt'
               this.ismembershipstillvalid = false
               return
            }
            var renewalYear = datevalid.year() + 1
            var availableFrom = datevalid.clone().add(1, 'day').format('DD/MM/YYYY')
            this.validmessage = 'Your membership is active until ' + datevalid.format('DD/MM/YYYY') + '. Renewal payment for ' + renewalYear + ' will be available from ' + availableFrom + '.'
            this.ismembershipstillvalid = true
            this.invalidmessage = ''
         }
      },

      paymentYearForSubmission() {
         return this.selectedPayYear || this.getPayYear()
      },

      initiateOnlinePayment(payyearOverride) {
         var payyear = payyearOverride || this.paymentYearForSubmission()
         this.setamount(this.form.membertype)

         var data = {
            userid: this.userid,
            membertype: this.paymentMemberType(),
            amount: this.form.amount,
            payyear: payyear.toString(),
            paymenttype: this.currentPaymentType(),
            description: this.form.description,
         }

         this.submitting = true
         this.showPaymentOptions = false
         axios.post('/member/payment/renewal/initiate', data)
         .then((resp) => {
            if (resp.data && resp.data.status === 'OK') {
               if (resp.data.data && resp.data.data.redirect_url) {
                  window.location.href = resp.data.data.redirect_url
               } else {
                  this.showPaymentMessage('success', resp.data.message || 'Payment already recorded')
                  this.loadpaymentinfo()
               }
            } else if (resp.data) {
               this.invalidmessage = resp.data.error
            }
         })
         .finally(() => {
            this.submitting = false
         })
      },

      sethaspaid() {
         var targetPath = '/member/payment/haspaid'
         var data = {
            userid: this.userid
         }
         axios.post(targetPath, data)
         .then((resp) => {
            if (resp.data) {
               var rtn = resp.data
               if (rtn.status !== 'OK') {
                  console.log(rtn.status + ': ' + rtn.error)
               }
            }
         })
      },

      registerreceipt() {
         var targetpath = "/member/payment/register"
         this.showPaymentMessage('info', '')

         if (!this.canRenewYear(this.paymentYearForSubmission())) {
            this.showPaymentMessage('warning', this.cpdLockText(this.paymentYearForSubmission()))
            this.dlgregreceipt = false
            return
         }

         var data = new FormData()
         data.append('userid', this.userid)
         data.append('description', this.form.description)
         data.append('membertype', this.paymentMemberType())
         data.append('paymenttype', this.currentPaymentType())
         data.append('amount', this.form.amount)
         data.append('paymentdate', this.$moment().format('YYYY-MM-DD HH:mm:ss'))
         data.append('payyear', this.paymentYearForSubmission().toString())
         data.append('trxnid', this.form.trxnid)
         if (this.form.document) {
            data.append('document', this.form.document)
         }

         this.submitting = true
         axios.post(targetpath, data, {
            headers: {
               'Content-Type': 'multipart/form-data'
            }
         })
         .then((resp) => {
            if (resp.data) {
               this.submitting = false
               var rtn = resp.data
               if (rtn.status === 'OK') {
                  var alreadyRecorded = String(rtn.message || '').toLowerCase().indexOf('already') >= 0
                  this.showPaymentMessage(alreadyRecorded ? 'info' : 'success', rtn.message || 'Receipt submitted for admin approval')
                  this.initialize()
                  this.dlgregreceipt = false
                  this.haspendingpayment = rtn.data && String(rtn.data.trxnstatus || '') === '00'
                  this.$emit('PaymentReady', this.haspaid || this.haspendingpayment)
               } else {
                  this.showPaymentMessage('error', rtn.error || 'Unable to submit receipt')
               }
            }
         })
         .catch((error) => {
            this.submitting = false
            this.showPaymentMessage('error', error.response?.data?.error || error.response?.data?.message || 'Unable to submit receipt')
         })

      }
   }

}
</script>

<style scoped>
.payment-card-title {
   align-items: flex-start;
   border-bottom: 1px solid #e6eaf0;
   padding-bottom: 14px;
}

.payment-title {
   color: #172b4d;
   font-size: 20px;
   font-weight: 700;
   letter-spacing: 0;
}

.payment-subtitle {
   color: #5e6c84;
   font-size: 13px;
   font-weight: 400;
   margin-top: 4px;
}

.payment-ledger-table {
   border: 1px solid #dfe4ea;
   border-radius: 6px;
   overflow: hidden;
}

.outstanding-payment-card {
   background: #fff7f7;
   border: 1px solid #f3b6b6;
   border-left: 5px solid #880808;
   border-radius: 6px;
   margin-bottom: 18px;
   overflow: hidden;
}

.outstanding-payment-header {
   align-items: center;
   display: flex;
   gap: 16px;
   justify-content: space-between;
   padding: 18px 18px 14px;
}

.outstanding-eyebrow {
   color: #880808;
   font-size: 11px;
   font-weight: 800;
   letter-spacing: .12em;
   text-transform: uppercase;
}

.outstanding-heading {
   color: #172b4d;
   font-size: 18px;
   font-weight: 800;
   letter-spacing: 0;
   margin-top: 3px;
}

.outstanding-copy {
   color: #5e6c84;
   font-size: 13px;
   margin-top: 4px;
}

.outstanding-total {
   background: #880808;
   border-radius: 6px;
   color: #fff;
   min-width: 170px;
   padding: 12px 16px;
   text-align: right;
}

.outstanding-total span {
   display: block;
   font-size: 11px;
   font-weight: 700;
   opacity: .85;
   text-transform: uppercase;
}

.outstanding-total strong {
   display: block;
   font-size: 22px;
   letter-spacing: 0;
   line-height: 1.2;
}

.outstanding-year-table {
   border-top: 1px solid #f3d0d0;
}

.outstanding-year-table ::v-deep thead tr {
   background: #fff;
}

.outstanding-year-table ::v-deep thead th {
   color: #344563;
   font-size: 12px;
   font-weight: 800;
   letter-spacing: .03em;
   text-transform: uppercase;
}

.outstanding-year {
   color: #172b4d;
   font-weight: 800;
}

.outstanding-note {
   color: #6b778c;
   font-size: 12px;
}

.cpd-lock-note {
   color: #7c2d12;
   font-size: 11px;
   font-weight: 600;
   line-height: 1.35;
   margin-top: 6px;
   max-width: 260px;
}

.payment-ledger-table ::v-deep thead tr {
   background: #f4f6f8;
}

.payment-ledger-table ::v-deep thead th {
   color: #344563;
   font-size: 12px;
   font-weight: 700;
   letter-spacing: .03em;
   text-transform: uppercase;
}

.payment-ledger-table ::v-deep tbody tr:hover {
   background: #f8fbff;
}

.payment-purpose {
   color: #172b4d;
   font-weight: 700;
}

.payment-description,
.payment-year,
.muted-text {
   color: #6b778c;
   font-size: 12px;
}

.payment-amount,
.payment-reference {
   color: #172b4d;
   font-weight: 600;
}

.payment-status-chip {
   border-radius: 999px !important;
   font-size: 11px !important;
   font-weight: 800 !important;
   height: 24px !important;
   letter-spacing: 0 !important;
   padding: 0 12px !important;
}

.payment-decline-note {
   background: #fffaf7;
   border: 1px solid #f1c7b8;
   border-left: 4px solid #b91c1c;
   border-radius: 6px;
   box-shadow: inset 0 1px 0 rgba(255, 255, 255, .75);
   color: #111827;
   margin-top: 8px;
   max-width: 300px;
   padding: 8px 10px;
}

.payment-decline-label {
   align-items: center;
   color: #7f1d1d;
   display: flex;
   font-size: 10px;
   font-weight: 800;
   letter-spacing: .04em;
   line-height: 1;
   margin-bottom: 4px;
   text-transform: uppercase;
}

.payment-decline-text {
   color: #1f2937;
   font-size: 12px;
   font-weight: 500;
   line-height: 1.4;
   overflow-wrap: anywhere;
}

.receipt-link {
   color: #0052cc;
   font-weight: 600;
   text-decoration: none;
}

.receipt-link:hover {
   text-decoration: underline;
}

.empty-payment-state {
   color: #6b778c;
   padding: 28px 12px !important;
}

.receipt-dialog {
   border-radius: 8px;
   overflow: hidden;
}

.receipt-dialog-title {
   align-items: flex-start;
   border-bottom: 1px solid #e6eaf0;
   padding: 20px 24px 16px;
}

.receipt-dialog-heading {
   color: #172b4d;
   font-size: 20px;
   font-weight: 700;
   letter-spacing: 0;
}

.receipt-dialog-subtitle {
   color: #6b778c;
   font-size: 13px;
   font-weight: 400;
   margin-top: 4px;
}

.receipt-dialog-actions {
   flex-wrap: wrap;
   border-top: 1px solid #e6eaf0;
   padding: 14px 24px 18px;
}

.receipt-dialog-alert {
   flex-basis: 100%;
   margin-bottom: 12px;
}
</style>
